import { useEffect, useState } from "react";
import type { MultiRetailerOverviewData } from "../types";
import { fetchOverviewData } from "../api/dashboardApi";

/**
 * Custom React hook to load multi-retailer overview data from the API.
 * API failures are exposed through the returned error state.
 *
 * Features:
 * - Loading states for smooth UX
 * - Error handling through returned state
 * - Consistent data shape with backend API expectations
 */
export function useMultiRetailerData(): {
  data: MultiRetailerOverviewData | null;
  loading: boolean;
  error: string | null;
} {
  const [data, setData] = useState<MultiRetailerOverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchOverviewData();
        if (cancelled) {
          return;
        }
        setData(result);
      } catch (err) {
        if (cancelled) {
          return;
        }
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        setError(errorMessage);
        setData(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
}
